import React from 'react';
import { PERF_MIN_EFFECT, shortSha, signed, unitSuffix } from '@chatsdet/types/perf';
import type { PerfRunHeadlineDelta, PerfVerdict } from '@chatsdet/types/perf';

/** A run's headline delta for the selected metric. */
export type PerfChartPoint = { runId: string; headSha: string; delta: PerfRunHeadlineDelta };

const WIDTH = 640;
const HEIGHT = 240;
const PAD = { top: 16, right: 16, bottom: 36, left: 56 };

const POINT_CLASS: Record<PerfVerdict, string> = {
  slower: 'fill-destructive stroke-destructive',
  faster: 'fill-success stroke-success',
  no_change: 'fill-muted-foreground stroke-muted-foreground',
};
const VERDICT_WORD: Record<PerfVerdict, string> = { slower: 'SLOWER', faster: 'faster', no_change: 'no change' };

/** How a point is drawn. Points are only comparable when they share a mode. */
type Mode = { key: string; scale: number; suffix: string; precision: number };

function modeOf({ unit, absolute }: PerfRunHeadlineDelta): Mode {
  if (unit === 'ratio') return { key: 'ratio', scale: 1, suffix: ' pt', precision: 1 };
  if (absolute) return { key: `abs:${unit}`, scale: 1, suffix: unitSuffix(unit), precision: 0 };
  return { key: 'pct', scale: 100, suffix: '%', precision: 1 };
}

/** Displayed delta and interval; rps is flipped like the verdict table (positive = faster). */
function shown(delta: PerfRunHeadlineDelta, mode: Mode) {
  const sign = delta.unit === 'rps' ? -1 : 1;
  const [a, b] = [delta.ci95[0] * sign * mode.scale, delta.ci95[1] * sign * mode.scale];
  return { value: delta.deltaPct * sign * mode.scale, low: Math.min(a, b), high: Math.max(a, b) };
}

/**
 * Page-local SVG chart: one median delta per run (point) with its 95% CI (bar), a zero line and
 * the minimum-effect band. `points` are newest first; they are drawn oldest to newest. Only
 * points sharing the newest point's unit mode are plotted, since relative and absolute deltas
 * do not share an axis. `packages/ui` has no interval or dot-plot chart.
 */
export function PerfDeltaChart({ points }: { points: PerfChartPoint[] }) {
  const newest = points[0];
  if (!newest) return null;
  const mode = modeOf(newest.delta);
  const plotted = points.filter((p) => modeOf(p.delta).key === mode.key).reverse();
  const omitted = points.length - plotted.length;
  const higherIsFaster = newest.delta.unit === 'rps';

  // The band is a fraction of the base median, so it only exists on percentage axes.
  const band = mode.key === 'pct' ? PERF_MIN_EFFECT * 100 : null;
  const spans = plotted.flatMap((p) => {
    const s = shown(p.delta, mode);
    return [s.low, s.high, s.value];
  });
  const limit = Math.max(...spans.map(Math.abs), band ?? 0) || 1;
  const extent = limit * 1.1;
  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const y = (v: number) => PAD.top + innerH / 2 - (v / extent) * (innerH / 2);
  const x = (i: number) => PAD.left + (plotted.length === 1 ? innerW / 2 : (i / (plotted.length - 1)) * innerW);
  const fmt = (v: number) => `${signed(v, mode.precision)}${mode.suffix}`;
  const direction = higherIsFaster ? 'above zero is faster' : 'above zero is slower';

  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full text-muted-foreground"
        role="img"
        aria-label={`${newest.delta.label} median delta with 95% confidence interval across ${plotted.length} runs; ${direction}`}
      >
        {band !== null && (
          <rect
            data-testid="perf-chart-band"
            x={PAD.left}
            y={y(band)}
            width={innerW}
            height={y(-band) - y(band)}
            className="fill-current opacity-10"
          />
        )}
        <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(0)} y2={y(0)} className="stroke-current" strokeDasharray="4 3" />
        <text x={PAD.left - 8} y={y(0)} textAnchor="end" dominantBaseline="middle" className="fill-current text-[11px]">
          0
        </text>
        <text x={PAD.left - 8} y={y(limit)} textAnchor="end" dominantBaseline="middle" className="fill-current text-[11px] tabular-nums">
          {fmt(limit)}
        </text>
        <text x={PAD.left - 8} y={y(-limit)} textAnchor="end" dominantBaseline="middle" className="fill-current text-[11px] tabular-nums">
          {fmt(-limit)}
        </text>
        {plotted.map(({ runId, headSha, delta }, i) => {
          const s = shown(delta, mode);
          return (
            <g key={runId} className={POINT_CLASS[delta.verdict]}>
              <title>{`${shortSha(headSha)}: ${VERDICT_WORD[delta.verdict]}, ${fmt(s.value)} (95% CI ${fmt(s.low)} .. ${fmt(s.high)}); ${direction}`}</title>
              <line x1={x(i)} x2={x(i)} y1={y(s.low)} y2={y(s.high)} strokeWidth={2} />
              <circle cx={x(i)} cy={y(s.value)} r={4} />
              <text x={x(i)} y={HEIGHT - 14} textAnchor="middle" className="fill-muted-foreground stroke-none font-mono text-[10px]">
                {shortSha(headSha)}
              </text>
            </g>
          );
        })}
      </svg>
      {omitted > 0 && (
        <p className="text-xs text-muted-foreground">
          {omitted} older {omitted === 1 ? 'run is' : 'runs are'} not shown: measured in different units.
        </p>
      )}
    </div>
  );
}
